import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import FadeLoader from 'react-spinners/FadeLoader';
import { redeemCode } from '../../actions/CodeUnlockActions';
import { shapesList } from '../../util/helpers/Helpers';
import './CodeUnlock.css';

/**
 * CodeUnlock
 *
 * Visual design: bold heading, outlined code input, solid "UNLOCK NOW" button.
 * Colors + border-radius follow competition data (buttonColor, secondaryColor,
 * defaultShape) exactly like every other button/panel in the app.
 *
 * API response shape expected from /packs/dc/redeem-code:
 * {
 *   data: [
 *     {
 *       id, idUnique, idPack,
 *       firstName, lastName,
 *       originalHeadshotUrl,   ← card image
 *       packName,
 *       packImageUrl,
 *       ...
 *     }
 *   ]
 * }
 *
 * Background is applied globally by AppShell / App (sets body/root styles
 * from competition data) — this component inherits it automatically.
 */
const CodeUnlock = ({
    comp,
    auth,
    codeUnlock,
    dispatch,
    onGoToCards,
    urlCode,
    onShowLogin,
    onShowRegister,
}) => {
    const [code, setCode]               = useState(urlCode || '');
    const [touched, setTouched]         = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);

    const pendingCode  = useRef(urlCode || null);
    const touchStartX  = useRef(null);
    const inputRef     = useRef(null);

    // Safe destructure — reducer may not be wired yet during first render
    const loading  = codeUnlock ? codeUnlock.loading  : false;
    const error    = codeUnlock ? codeUnlock.error    : false;
    const cards    = codeUnlock ? codeUnlock.cards    : [];
    const redeemed = codeUnlock ? codeUnlock.redeemed : false;

    const loggedIn = auth ? auth.loggedIn : false;
    const token    = localStorage.getItem('token');

    // ── Derive shape/color from competition data ──────────────────────────
    // mirrors how every other button/container in the app works
    const shapeClass   = comp ? (shapesList[comp.defaultShape] || '') : '';
    const buttonBg     = comp ? (comp.buttonColor    || '#000000') : '#000000';
    const buttonColor  = comp ? (comp.secondaryColor || '#ffffff') : '#ffffff';
    const primaryColor = comp ? (comp.primaryColor   || '#000000') : '#000000';

    // ── Sync URL code ─────────────────────────────────────────────────────
    useEffect(() => {
        if (urlCode) {
            setCode(urlCode);
            pendingCode.current = urlCode;
        }
    }, [urlCode]);

    // ── Auto-submit once authenticated ────────────────────────────────────
    useEffect(() => {
        if (loggedIn && pendingCode.current && !redeemed && !loading) {
            dispatch(redeemCode({ code: pendingCode.current, token }, dispatch));
        }
    }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Reset carousel index on fresh results ─────────────────────────────
    useEffect(() => {
        setCarouselIndex(0);
    }, [cards]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleUnlock = () => {
        setTouched(true);
        if (!code.trim()) {
            inputRef.current && inputRef.current.focus();
            return;
        }
        pendingCode.current = code.trim();

        if (!loggedIn) {
            onShowLogin && onShowLogin();
            return;
        }
        dispatch(redeemCode({ code: code.trim(), token }, dispatch));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleUnlock();
    };

    const prev = () =>
        setCarouselIndex(i => (i - 1 + cards.length) % cards.length);
    const next = () =>
        setCarouselIndex(i => (i + 1) % cards.length);

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 40) delta > 0 ? next() : prev();
        touchStartX.current = null;
    };

    // ── Loader — identical pattern to App.js ──────────────────────────────
    if (loading) {
        return (
            <div className="cu-loading-wrap">
                <div className="loaderCss">
                    <div className="loader">
                        <FadeLoader size={150} color="#ffffff" loading={true} />
                    </div>
                </div>
            </div>
        );
    }

    // ── Carousel — shown after successful redemption ──────────────────────
    if (redeemed && cards && cards.length > 0) {
        return (
            <div className="cu-container desc-font">
                <div className="cu-result">
                    <h2 className="cu-result__title" style={{ color: primaryColor }}>
                        🎉 You unlocked {cards.length} card{cards.length > 1 ? 's' : ''}!
                    </h2>

                    <div
                        className="cu-carousel"
                        onTouchStart={onTouchStart}
                        onTouchEnd={onTouchEnd}
                    >
                        {cards.length > 1 && (
                            <button
                                className="cu-carousel__arrow cu-carousel__arrow--left"
                                onClick={prev}
                                aria-label="Previous card"
                            >
                                &#8249;
                            </button>
                        )}

                        <div className="cu-carousel__track">
                            {cards.map((card, idx) => (
                                <div
                                    key={card.idUnique || idx}
                                    className={`cu-carousel__slide ${idx === carouselIndex ? 'cu-carousel__slide--active' : ''}`}
                                >
                                    <div className={`cu-card ${shapeClass}`}>
                                        {/* Show headshot if available, fallback to pack image */}
                                        {(card.originalHeadshotUrl || card.packImageUrl) && (
                                            <img
                                                src={card.originalHeadshotUrl || card.packImageUrl}
                                                alt={`${card.firstName} ${card.lastName}`}
                                                className="cu-card__image"
                                            />
                                        )}
                                        {/* <div className="cu-card__info">
                                            {card.firstName || card.lastName ? (
                                                <p className="cu-card__name" style={{ color: primaryColor }}>
                                                    {card.firstName} {card.lastName}
                                                </p>
                                            ) : null}
                                            {card.packName && (
                                                <p className="cu-card__pack">{card.packName}</p>
                                            )}
                                        </div>*/}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {cards.length > 1 && (
                            <button
                                className="cu-carousel__arrow cu-carousel__arrow--right"
                                onClick={next}
                                aria-label="Next card"
                            >
                                &#8250;
                            </button>
                        )}

                        {cards.length > 1 && (
                            <div className="cu-carousel__dots">
                                {cards.map((_, idx) => (
                                    <span
                                        key={idx}
                                        className={`cu-carousel__dot ${idx === carouselIndex ? 'cu-carousel__dot--active' : ''}`}
                                        style={idx === carouselIndex ? { background: buttonBg } : {}}
                                        onClick={() => setCarouselIndex(idx)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        className={`cu-btn ${shapeClass}`}
                        style={{ backgroundColor: buttonBg, color: buttonColor }}
                        onClick={onGoToCards}
                    >
                        View My Cards Collection →
                    </button>
                </div>
            </div>
        );
    }

    // ── Code-entry form — matches the mockup ─────────────────────────────
    return (
        <div className="cu-container desc-font">
            <div className="cu-form">
                <h1 className="cu-form__title" style={{ color: primaryColor }}>
                    Unlock your new<br />player or pack!
                </h1>

                {/* Outlined input — matches mockup exactly */}
                <div className="cu-form__row">
                    <input
                        ref={inputRef}
                        className={`cu-input ${shapeClass} ${touched && !code.trim() ? 'cu-input--error' : ''}`}
                        type="text"
                        placeholder="ENTER CODE"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                    />
                </div>

                {touched && !code.trim() && (
                    <p className="cu-error-msg">Please enter a code.</p>
                )}
                {error && (
                    <p className="cu-error-msg">
                        Invalid code. Please check and try again.
                    </p>
                )}

                {/* Solid button — uses competition buttonColor + secondaryColor */}
                <button
                    className={`cu-btn ${shapeClass}`}
                    style={{ backgroundColor: buttonBg, color: buttonColor }}
                    onClick={handleUnlock}
                >
                    UNLOCK NOW
                </button>

                {/* Login / register nudge for unauthenticated users */}
                {!loggedIn && (
                    <p className="cu-auth-hint">
                        Have an account?{' '}
                        <span
                            className="cu-auth-link"
                            style={{ color: buttonBg }}
                            onClick={() => onShowLogin && onShowLogin()}
                        >
                            Log in
                        </span>
                        {' '}or{' '}
                        <span
                            className="cu-auth-link"
                            style={{ color: buttonBg }}
                            onClick={() => onShowRegister && onShowRegister()}
                        >
                            Sign up
                        </span>
                        {' '}to save your cards.
                    </p>
                )}
            </div>
        </div>
    );
};

/**
 * State shape:
 *   state.default  — the rootReducer (auth, competition, tracking, locale)
 *   state.codeUnlock — standalone slice added to combineReducers in index.js
 */
const mapStateToProps = (state, ownProps) => ({
    ...ownProps,
    comp:        state.default ? state.default.competition.data : null,
    auth:        state.default ? state.default.auth : {},
    codeUnlock:  state.codeUnlock || { loading: false, error: false, cards: [], redeemed: false },
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(CodeUnlock);
